import { match } from "ts-pattern";
import { z } from "zod";

type StepStateType = { state: "success" | "pending" } | { state: "failed"; error: unknown };

export const ID = z.number();
export const Count = z
  .object({ count: z.number() })
  .array()
  .transform((value) => value.reduce((acc, suite) => acc + suite.count, 0));

export const ImgNullable = z.string().nullable();

const KnownActionTypeSchema = z.enum([
  "tap",
  "swipe",
  "text",
  "long_press",
  "back",
  "tap_grid",
  "swipe_grid",
  "long_press_grid",
  "finish",
  "hide_keyboard",
]);

export const ActionTypeSchema = z.union([KnownActionTypeSchema, z.string()]);
export type ActionType = z.infer<typeof KnownActionTypeSchema>;
export const DirectionSchema = z.enum(["up", "down", "left", "right"]);
export type Direction = z.infer<typeof DirectionSchema>;
export const DistanceSchema = z.enum(["short", "medium", "long"]);
export type Distance = z.infer<typeof DistanceSchema>;
export const DecisionSchema = z.enum(["success", "failure"]);
export type Decision = z.infer<typeof DecisionSchema>;
export const ElementTypeSchema = z.enum(["xml", "uied", "unknown"]);
export type ElementType = z.infer<typeof ElementTypeSchema>;
export const ReflectDecisionSchema = z.enum(["back", "ineffective", "continue", "success"]);
export type ReflectDecision = z.infer<typeof ReflectDecisionSchema>;

export const BBoxSchema = z.array(z.array(z.number()).length(2)).length(2);
export const UiElementSchemaBase = z.object({
  uid: z.string(),
  bbox: BBoxSchema,
});

export const AndroidElementSchema = UiElementSchemaBase.extend({
  attrib: z.string(),
  detection_type: z.literal("xml"),
});

export const OmniparserUiElementSchema = UiElementSchemaBase.extend({
  text: z.string().nullable(),
  type: z.enum(["text", "icon"]),
  detection_type: z.literal("omniparser"),
});

export const UiedUiElementSchema = UiElementSchemaBase.extend({
  text: z.string().nullable(),
  detection_type: z.literal("uied"),
});

export const UiElementSchema = z.discriminatedUnion("detection_type", [
  AndroidElementSchema,
  OmniparserUiElementSchema,
  UiedUiElementSchema,
]);

export const ActionSchema = z
  .object({
    action_type: ActionTypeSchema.nullable(),
    ui_element: z.lazy(() => UiElementSchema).nullable(),
    params: z.array(z.union([z.string(), DistanceSchema, DirectionSchema])).nullable(),
    label_number: z
      .number()
      .int()
      .nullable()
      .transform((value) => (typeof value === "number" ? value : -1)),
    user_description: z.string().nullable(),
  })
  .transform((value) => {
    const text =
      value.user_description ||
      match(value.action_type)
        .with("tap", () => `Tap on an interactive element with label "${value.label_number}"`)
        .with("text", () => `Type "${value.params?.join("; ")}"`)
        .with("finish", () => "Finished the test")
        .with("hide_keyboard", () => "Hide keyboard")
        .with("back", () => "Go back")
        .otherwise((action) => {
          if (action) console.warn(`[Unknown action] Received unrecognized action type:`, action);
          return typeof action === "string" ? action : "unknown";
        });
    return { ...value, text };
  });

export type Action = z.infer<typeof ActionSchema>;

export const CheckAnomaliesResponseSchema = z.object({
  decision: DecisionSchema,
  explanation: z.string(),
});

export const LogCheckAnomaliesSchema = z.object({
  images: z.array(z.string()),
  log_embeddings: z.string().nullish(),
  prompt: z.string().nullable(),
  response: z.lazy(() => CheckAnomaliesResponseSchema).nullish(),
});

export const LogDocumentationSchema = z.object({
  prompt: z.string(),
  response: z.object({ description: z.string() }),
  images: z.array(z.string()),
});

export const ActionResponseSchema = z.object({
  action_type: ActionTypeSchema,
  params: z.array(z.union([z.number().int(), DirectionSchema, DistanceSchema, z.string()])).nullable(),
});

export const LogExploreSchema = z.object({
  prompt: z.string(),
  response: z
    .object({
      observation: z.string(),
      thought: z.string(),
      action: ActionResponseSchema,
      summary: z.string(),
    })
    .nullish(),
  images: z.array(z.string()),
});

export const ReflectResponseSchema = z.object({
  decision: ReflectDecisionSchema,
  thought: z.string(),
  documentation: z.string().nullable(),
});

export const LogReflectSchema = z.object({
  prompt: z.string(),
  response: z.lazy(() => ReflectResponseSchema).nullish(),
  images: z.array(z.string()),
});

export const CheckResultResponseSchema = z.object({
  decision: DecisionSchema,
  explanation: z
    .string()
    .describe(
      "Describe the requirements that have been met, those that have not been met, and those that cannot be determined",
    ),
});

export const LogCheckResultSchema = z.object({
  prompt: z.string().nullable(),
  response: CheckResultResponseSchema.nullable(),
  images: z.array(z.string()),
  log_user_decision: z.string().nullable(),
});

export type StepViewType = {
  text: string;
  label: string;
  images: ({ src: string; alt: string } | null)[];
  logs: {
    status?: {
      type: "success" | "failure";
      message: string;
    };
    data: any;
  } | null;
};

export interface GalleryImage {
  src: string;
  alt: string;
  title?: string;
  state?: StepStateType;
  round: number;
  description?: string;
  stepIndex?: number;
  action?: Action;
}

export const StateTransformer = z.object({ state: z.string() }).transform((value) => value.state);
export const PublicIdTransformer = z.object({ public_id: z.string() }).transform((value) => value.public_id);
export const NameTransformer = z.object({ name: z.string() }).transform((value) => value.name);

export const CycleType = z.enum(["learn", "relearn", "run_follow_path"]).transform((value) => {
  if (value === "run_follow_path") return { name: "run" as const, label: "Run" };
  if (value === "learn") return { name: "learn" as const, label: "Learn" };
  if (value === "relearn") return { name: "relearn" as const, label: "Relearn" };
  throw new Error(`Invalid cycle type: ${value}`);
});

export const CyclePhaseType = z.enum(["learn", "relearn", "run", "run_relearn", "rerun", "unknown"]);
export const StepType = z.enum(["beg_task", "end_step", "end_task"]);
export const FileTypes = z.enum(["video", "image", "binary", "text", "log"]);
export const HistoryItemState = z.enum(["success", "skip", "running", "failure", "anomalies_detected"]);
export const ListSchema = <TItem>(itemSchema: z.ZodSchema<TItem>) =>
  z.object({ items: z.array(itemSchema), count: z.number() });

/** Generic success envelope returned by action endpoints (e.g. invitation cancel). */
export const SuccessOut = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const DecimalNumber = z
  .union([z.number(), z.string().regex(/^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/)])
  .pipe(z.coerce.number().finite());

export const StorageUsageObjectSchema = z.object({
  giga_days: z.number(),
  giga_hours: z.number(),
});

export const UsageStatisticsSchema = z.object({
  total_input_tokens: z.number().nullable().default(null),
  total_output_tokens: z.number().nullable().default(null),
  total_tokens: z.number().nullable().default(null),
  total_cost: DecimalNumber.nullable().default(null),
  total_cost_currency: z.string().nullable().default(null),
  file_storage_cost: DecimalNumber.default(0),
  artifact_storage_cost: DecimalNumber.default(0),
  total_storage_cost: DecimalNumber.default(0),
  total_calculated_cost: DecimalNumber.default(0),
  total_artifact_storage: StorageUsageObjectSchema.nullable().default(null),
  total_file_storage: StorageUsageObjectSchema.nullable().default(null),
  total_worker_seconds: z.number().nullable().default(null),
  total_worker_cost: DecimalNumber.default(0),
});

/** Backend sends every ratio as a display string plus the raw 0..1 value. */
export const RatioValue = z.object({
  percentage: z.string(),
  value: z.number(),
});
